import { DISTRICT_FX, DISTRICT_KINDS, GREAT_WORK_KEEP_FRAIL_BP, type DistrictKind } from '@somnhold/content';
import { activeHeroes, eraOf, freeSides, greatWorkCost, groupLevel, nextDistrict, withDistrict, type CampaignCommand, type CampaignState, heroName } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';

/** A district being planned: the kind picked and the side shown on the board. */
export interface DistrictPlan {
  side: number;
  kind: DistrictKind;
}

export const DISTRICT_ICON: Record<DistrictKind, string> = {
  military: ICONS.sword,
  quarry: ICONS.stone,
  sacred: ICONS.heart,
  guild: ICONS.hammer,
};

const pct = (bp: number): number => Math.round(Math.abs(bp - 10000) / 100);

/** What a kind of district does, in words, with its numbers from the data. */
export const districtDesc = (k: DistrictKind): string =>
  k === 'military'
    ? t('district.military.desc', { n: pct(DISTRICT_FX.military.costBp) })
    : k === 'sacred'
      ? t('district.sacred.desc', { n: pct(10000 + DISTRICT_FX.sacred.healBp) })
      : k === 'quarry'
        ? t('district.quarry.desc', { wood: DISTRICT_FX.quarry.perPlayer.wood, stone: DISTRICT_FX.quarry.perPlayer.stone })
        : t(`district.${k}.desc`);

export const eraName = (n: number): string => t(`era.${Math.min(5, Math.max(1, n))}`);

/** The side nearest a point on the board (sim units) among those that can take a district. */
export const nearestSide = (s: CampaignState, x: number, y: number, centerOf: (side: number) => { x: number; y: number }): number | null => {
  let best: number | null = null;
  let bd = Infinity;
  const a = Math.atan2(y, x);
  for (const side of freeSides(s)) {
    const c = centerOf(side);
    let d = Math.abs(Math.atan2(c.y, c.x) - a);
    if (d > Math.PI) d = 2 * Math.PI - d;
    if (d < bd) {
      bd = d;
      best = side;
    }
  }
  return best;
};

interface Ctx {
  s: CampaignState;
  now: number;
  plan: DistrictPlan | null;
  costChips: (c: { wood: number; stone: number; crystal: number }, have?: { wood: number; stone: number; crystal: number }) => HTMLElement;
  setPlan: (p: DistrictPlan | null) => void;
  cmd: (c: CampaignCommand) => void;
}

/**
 * The keep's sheet when no Great Work stands on it: the era, what the next district asks (level of the
 * active heroes, cost, time) and, once planning, the kind and the side.
 */
export const keepPanel = (c: Ctx): HTMLElement => {
  const { s } = c;
  const era = eraOf(s);
  const box = h('div.great');
  box.append(
    h('div.great-head', {}, h('span.great-ico', { html: ICONS.keep }), h('div', {}, h('h3', {}, t('bastion.keep')), h('span.tag.era', {}, eraName(era)))),
    h('div.bar.green', {}, h('i', { style: `width:${(100 * s.keep.hp) / Math.max(1, s.keep.maxHp)}%` })),
    h('div.small', {}, `${s.keep.hp} / ${s.keep.maxHp}`),
  );
  const districts = s.map.districts ?? [];
  if (districts.length) {
    const row = h('div.great-districts');
    for (const d of districts) row.append(h('span.chip-moment', {}, h('span', { html: DISTRICT_ICON[d.kind] }), t(`district.${d.kind}`)));
    box.append(row);
  }
  const next = nextDistrict(s);
  const work = h('section.great-work', {}, h('h4', {}, t('great.title')));
  box.append(work);
  if (!next) {
    work.append(h('p.hint', {}, t('great.maxed')));
    return box;
  }
  // While planning, the sheet keeps to the choice (on a phone it shares the screen with the board).
  if (!c.plan) work.append(h('p.hint', {}, next.era > eraOf(s) ? t('great.intro', { era: eraName(next.era) }) : t('great.introMore', { era: eraName(next.era) })));
  // The era asks the group's level: the average of the active heroes (who pulls it down shows first).
  const group = groupLevel(s, c.now);
  const below = group < next.level;
  if (!c.plan && below) {
    const active = [...activeHeroes(s, c.now)].sort((a, b) => a.hero.level - b.hero.level);
    work.append(
      h('p.small', {}, t('great.needLevel', { n: next.level, g: group })),
      h('div.great-below', {}, h('span.small', {}, t('great.below')), ...active.map((p) => h('span.who-chip', { style: `--who:${p.color}` }, `${heroName(p)} · ${t('bastion.level', { n: p.hero.level })}`))),
    );
  } else if (!c.plan) work.append(h('p.small.ok', {}, t('great.ready', { n: next.level })));
  work.append(
    h('div.cost', {}, c.costChips(greatWorkCost(s), s.storage), h('span.cost-time', {}, h('span', { html: ICONS.hourglass }), fmtDuration(next.buildMs))),
    c.plan ? '' : h('p.small.warn', {}, t('great.frail', { n: pct(GREAT_WORK_KEEP_FRAIL_BP) })),
  );
  const sides = freeSides(s);
  if (next.crown) {
    // Eras IV on crown the keep: no side, no kind; the Work goes straight onto the keep.
    work.append(h('p.small', {}, t('great.crownDesc')), h('div.actions', {}, h('button.btn.small', { 'data-act': 'great-crown', disabled: below, onclick: () => c.cmd({ t: 'greatWork' }) }, t('great.crown'))));
    return box;
  }
  if (!c.plan) {
    work.append(h('div.actions', {}, h('button.btn.small', { 'data-act': 'great-plan', disabled: below || sides.length === 0, onclick: () => c.setPlan({ side: sides[0] ?? 0, kind: 'military' }) }, t('great.plan'))));
    return box;
  }
  // Planning: the kind, then the side (the board shows the district where it would rise).
  const plan = c.plan;
  const kinds = h('div.district-pick', { role: 'radiogroup', 'aria-label': t('great.pickKind') });
  for (const k of DISTRICT_KINDS) {
    kinds.append(
      h(
        `button.district${k === plan.kind ? '.on' : ''}`,
        { 'data-district': k, role: 'radio', 'aria-checked': String(k === plan.kind), title: districtDesc(k), onclick: () => c.setPlan({ ...plan, kind: k }) },
        h('span.d-ico', { html: DISTRICT_ICON[k] }),
        h('span.nm', {}, t(`district.${k}.short`)),
      ),
    );
  }
  const i = Math.max(0, sides.indexOf(plan.side));
  const step = (d: number): void => c.setPlan({ ...plan, side: sides[(i + d + sides.length) % sides.length] ?? plan.side });
  const ok = !!withDistrict(s.map, plan);
  work.append(
    h('h4.small', {}, t('great.pickKind')),
    kinds,
    h('p.district-desc', {}, h('b', {}, t(`district.${plan.kind}`)), ' — ', districtDesc(plan.kind)),
    h('p.small', {}, t('great.pickSide')),
    h(
      'div.side-pick',
      {},
      h('button.iconbtn', { 'aria-label': t('prev'), 'data-act': 'side-prev', onclick: () => step(-1), html: '&#8249;' }),
      h('b', {}, t('great.sideN', { n: plan.side + 1 })),
      h('button.iconbtn', { 'aria-label': t('next'), 'data-act': 'side-next', onclick: () => step(1), html: '&#8250;' }),
    ),
    h(
      'div.actions',
      {},
      h('button.btn.small', { 'data-act': 'great-place', disabled: !ok, onclick: () => c.cmd({ t: 'greatWork', side: plan.side, kind: plan.kind }) }, t('great.place')),
      h('button.btn.small.ghost', { onclick: () => c.setPlan(null) }, t('great.cancel')),
    ),
  );
  return box;
};
